import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSiteContent } from "../../context/SiteContentContext";
import { Lines } from "../../components/site/siteText";
import { API_BASE } from "../../services/apiClient";
import { CATEGORIES, submitAnnouncement } from "../../services/newspaperApi";
import { FORM_GROUPS } from "../../forms/formTypes";
import { addToBasket, readBasket } from "../../services/noticeBasket";
import "./newspaper.css";
import DateInput from "../../components/site/DateInput";

// Illustrations from the reference method cards
const METHOD_ART = [
  <svg key="t" viewBox="0 0 320 190" role="img" aria-label="กรอกข้อความประกาศด้วยตนเอง"><rect width="320" height="190" fill="#efeee7" /><circle cx="270" cy="35" r="54" fill="#e1e8ee" /><rect x="75" y="24" width="160" height="143" rx="3" fill="white" stroke="#9faebe" /><rect x="96" y="44" width="100" height="9" fill="#19345f" /><path d="M96 70H211" stroke="#b1bdc7" strokeWidth="3" /><path d="M96 87H211" stroke="#b1bdc7" strokeWidth="3" /><path d="M96 104H211" stroke="#b1bdc7" strokeWidth="3" /><path d="M96 121H211" stroke="#b1bdc7" strokeWidth="3" /><path d="M96 138H168" stroke="#b1bdc7" strokeWidth="3" /><path d="m209 115 17-35 10 5-17 35-12 11z" fill="#b8953c" /></svg>,
  <svg key="f" viewBox="0 0 320 190" role="img" aria-label="แนบ PDF หรือรูปภาพ"><rect width="320" height="190" fill="#efeee7" /><circle cx="270" cy="35" r="54" fill="#e1e8ee" /><rect x="75" y="24" width="160" height="143" rx="3" fill="white" stroke="#9faebe" /><rect x="96" y="46" width="70" height="30" fill="#19345f" /><text x="110" y="67" fill="#fff" fontSize="17">PDF</text><rect x="122" y="89" width="104" height="58" fill="#e8eee8" stroke="#83978e" /><circle cx="148" cy="105" r="7" fill="#d4b86f" /><path d="m129 139 26-22 16 12 20-25 27 35z" fill="#839c96" /></svg>,
  <svg key="e" viewBox="0 0 320 190" role="img" aria-label="กรอก Excel ให้แอดมินนำเข้า"><rect width="320" height="190" fill="#efeee7" /><circle cx="270" cy="35" r="54" fill="#e1e8ee" /><rect x="75" y="24" width="160" height="143" rx="3" fill="white" stroke="#9faebe" /><text x="98" y="49" fill="#19345f" fontSize="14">EXCEL</text><rect x="95" y="62" width="122" height="78" fill="#f5f7f2" stroke="#b7c4b3" /><path d="M95 88H217" stroke="#b7c4b3" /><path d="M95 114H217" stroke="#b7c4b3" /><path d="M135 62V140" stroke="#b7c4b3" /><path d="M177 62V140" stroke="#b7c4b3" /><rect x="95" y="62" width="122" height="16" fill="#19345f" /><circle cx="231" cy="143" r="22" fill="#b8953c" /><path d="m221 143 7 7 14-16" stroke="#fff" strokeWidth="3" fill="none" /></svg>,
];
const METHOD_KEYS = ["template", "file", "excel"];

function MethodChoice({ c }) {
  const methods = Array.isArray(c.methods) ? c.methods : [];
  return (
    <>
      <div className="section-heading">
        <div>
          <p className="eyebrow">{c.eyebrow}</p>
          <h1><Lines text={c.title} /></h1>
        </div>
        <p><Lines text={c.description} /></p>
      </div>
      <section className="method-choice-grid">
        {methods.slice(0, 3).map((m, i) => (
          <Link key={i} className="method-choice" to={`/newspaper/submit?method=${METHOD_KEYS[i]}`}>
            {METHOD_ART[i]}
            <div>
              <span>{m.label}</span>
              <h2>{m.title}</h2>
              <p>{m.text}</p>
              <strong>{c.chooseLabel}</strong>
            </div>
          </Link>
        ))}
      </section>
      <div className="publication-note">
        <span>↳</span>
        <p>{c.note}</p>
      </div>
    </>
  );
}

function Guidance({ c }) {
  const steps = Array.isArray(c.guidanceSteps) ? c.guidanceSteps : [];
  return (
    <aside className="submit-guidance">
      <p className="eyebrow">{c.guidanceEyebrow}</p>
      <h3>{c.guidanceTitle}</h3>
      <ol>{steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
      <div>
        <h4>{c.paymentTitle}</h4>
        {c.priceNote && <p className="submit-price-note"><strong>{c.priceNote}</strong></p>}
        <p>{c.paymentText}</p>
      </div>
      <Link className="text-link" to="/newspaper/status">
        ติดตามสถานะประกาศ{" "}
        <span>→</span>
      </Link>
    </aside>
  );
}

function TemplateMethod({ c }) {
  const method = (Array.isArray(c.methods) && c.methods[0]) || {};
  return (
    <>
      <div className="section-heading">
        <div>
          <p className="eyebrow">{method.label}</p>
          <h1>{method.title}</h1>
        </div>
        <p>{method.text}</p>
      </div>
      <div className="submission-layout">
        <div className="submission-form-card">
          <p className="eyebrow">เลือกประเภทประกาศ</p>
          {FORM_GROUPS.map((g) => (
            <React.Fragment key={g.group}>
              <p className="form-type-group">{g.group}</p>
              <div className="form-type-list">
                {g.forms.map((f) => (
                  <Link key={f.path} to={`${f.path}?submit=1`}>
                    {f.name}
                    <span>↗</span>
                  </Link>
                ))}
              </div>
            </React.Fragment>
          ))}
          <p className="adm-note">กรอกข้อมูลในแบบฟอร์ม ดูตัวอย่างหน้าประกาศ แล้วกด &quot;ส่งประกาศ&quot; ด้านบนของแบบฟอร์ม</p>
        </div>
        <Guidance c={c} />
      </div>
    </>
  );
}

const EMPTY = { title: "", category: CATEGORIES[0], companyName: "", contactName: "", phone: "", email: "", requestedDate: "", summary: "" };

async function downloadExcelTemplate() {
  const res = await fetch(`${API_BASE}/api/excel/template/download`);
  if (!res.ok) throw new Error("ดาวน์โหลดไฟล์ต้นแบบไม่สำเร็จ");
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = "maneenopkao-announcement-template.xlsx";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function FileMethod({ c, method }) {
  const isExcel = method === "excel";
  const info = (Array.isArray(c.methods) && c.methods[isExcel ? 2 : 1]) || {};
  const [form, setForm] = useState(() => ({ ...EMPTY, ...readBasket().contact })); // the next notice starts with the same contact
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const previews = useMemo(
    () => files.filter((f) => /^image\//.test(f.type)).map((f) => ({ name: f.name, url: URL.createObjectURL(f) })),
    [files]
  );
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (files.length === 0) { setError("กรุณาแนบไฟล์อย่างน้อย 1 ไฟล์"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("method", method);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f, f.name));
      const res = await submitAnnouncement(fd);
      // contact + title go into the basket (the next notice starts filled in; the payment needs the phone)
      setDone({ ...res, phone: form.phone, contactName: form.contactName, companyName: form.companyName, title: form.title });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Success c={c} result={done} />;

  return (
    <>
      <div className="section-heading">
        <div>
          <p className="eyebrow">{info.label}</p>
          <h1>{info.title}</h1>
        </div>
        <p>{info.text}</p>
      </div>
      <div className="submission-layout">
        <div className="submission-form-card">
          <p className="eyebrow">{isExcel ? "ส่ง EXCEL ให้แอดมิน" : "แนบไฟล์ประกาศ"}</p>
          {isExcel && (
            <button type="button" className="button outline template-download" onClick={() => downloadExcelTemplate().catch((err) => setError(err.message))}>
              ดาวน์โหลดไฟล์ต้นแบบ Excel{" "}
              <span>↓</span>
            </button>
          )}
          <form className="site-form" onSubmit={submit}>
            {error && <p className="form-error" role="alert">{error}</p>}
            <label>
              ชื่อประกาศ <span className="required">*</span>
              <input required value={form.title} onChange={set("title")} placeholder={isExcel ? "เช่น ประกาศเชิญประชุม 5 บริษัท" : "เช่น ประกาศเชิญประชุมผู้ถือหุ้น"} />
            </label>
            <div className="form-row">
              <label>
                ประเภทเนื้อหา
                <select value={form.category} onChange={set("category")}>
                  {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
                </select>
              </label>
              <label>
                วันที่ต้องการลงประกาศ
                <DateInput value={form.requestedDate} onChange={set("requestedDate")} />
              </label>
            </div>
            <label>
              บริษัท / กิจการ
              <input value={form.companyName} onChange={set("companyName")} autoComplete="organization" />
            </label>
            <div className="form-row">
              <label>
                ชื่อผู้ติดต่อ <span className="required">*</span>
                <input required value={form.contactName} onChange={set("contactName")} autoComplete="name" />
              </label>
              <label>
                เบอร์โทร <span className="required">*</span>
                <input required type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" />
                <small>ใช้คู่กับเลขติดตามเพื่อดูสถานะประกาศ</small>
              </label>
            </div>
            <label>
              อีเมล
              <input type="email" value={form.email} onChange={set("email")} autoComplete="email" />
            </label>
            <label>
              หมายเหตุถึงกองบรรณาธิการ
              <textarea rows={3} value={form.summary} onChange={set("summary")} />
            </label>

            <div className="file-drop">
              <input
                type="file"
                multiple
                accept={isExcel ? ".xlsx,.xls" : ".pdf,.jpg,.jpeg,.png"}
                onChange={(e) => {
                  // check here, before a 60 MB upload is refused by the server
                  const picked = Array.from(e.target.files || []);
                  const okExt = isExcel ? /\.xlsx?$/i : /\.(pdf|jpe?g|png)$/i;
                  const problems = [];
                  if (picked.length > 6) problems.push(`แนบได้สูงสุด 6 ไฟล์ (เลือกมา ${picked.length} ไฟล์ ใช้ 6 ไฟล์แรก)`);
                  const kept = picked.slice(0, 6).filter((f) => {
                    if (!okExt.test(f.name)) { problems.push(`${f.name}: ชนิดไฟล์ไม่รองรับ`); return false; }
                    if (f.size > 10 * 1024 * 1024) { problems.push(`${f.name}: ใหญ่เกิน 10 MB`); return false; }
                    return true;
                  });
                  setFiles(kept);
                  setError(problems.join(" · "));
                }}
              />
              <small>{isExcel ? "รองรับ .xlsx และ .xls" : "รองรับ PDF, JPG และ PNG"} · สูงสุด 6 ไฟล์ ไฟล์ละไม่เกิน 10 MB</small>
              {files.length > 0 && <small>เลือกแล้ว: {files.map((f) => f.name).join(", ")}</small>}
            </div>

            {previews.length > 0 && (
              <div className="local-file-previews">
                {previews.map((p) => (
                  <figure key={p.url}>
                    <img src={p.url} alt={p.name} />
                    <figcaption>{p.name}</figcaption>
                  </figure>
                ))}
              </div>
            )}

            <button className="button dark form-submit" type="submit" disabled={busy} style={{ marginTop: 25 }}>
              {busy ? "กำลังส่ง..." : "ส่งประกาศ"}{" "}
              <span>↗</span>
            </button>
          </form>
        </div>
        <Guidance c={c} />
      </div>
    </>
  );
}

// After a notice is saved (owner 28-10-2569): it joins "ประกาศที่รอชำระเงิน" in this browser; the customer can send
// the next notice, or go to /newspaper/checkout to pay one total for all of them (payment + contact admin there).
export function Success({ c, result }) {
  const [state] = useState(() => addToBasket(
    { trackingCode: result.trackingCode, title: result.title },
    { contactName: result.contactName, phone: result.phone, companyName: result.companyName }
  ));
  const items = state.basket.items;
  return (
    <div className="submission-success">
      <span>✓</span>
      <h2>{c.successTitle}</h2>
      <p>บันทึกประกาศเรื่องที่ {items.length} แล้ว · เลขติดตาม <strong>{result.trackingCode}</strong></p>
      {state.restarted && <p className="adm-note">ประกาศก่อนหน้าใช้เบอร์โทรอื่น จึงเริ่มรายการชำระเงินใหม่สำหรับเบอร์นี้</p>}
      <div className="basket-box">
        <p className="eyebrow">ประกาศที่รอชำระเงิน · {items.length} เรื่อง</p>
        <ol>
          {items.map((i) => <li key={i.trackingCode}><strong>{i.trackingCode}</strong> · {i.title}</li>)}
        </ol>
        <p className="adm-note">มีประกาศมากกว่า 1 เรื่อง? ลงให้ครบก่อน แล้วชำระเงินรวมครั้งเดียว</p>
        <div className="basket-actions">
          <Link className="button outline" to="/newspaper/submit">
            + ลงประกาศเรื่องถัดไป
          </Link>
          <Link className="button dark" to="/newspaper/checkout">
            เสร็จแล้ว ไปชำระเงิน ({items.length} เรื่อง){" "}
            <span>→</span>
          </Link>
        </div>
      </div>
      <Link className="text-link" to="/newspaper">
        กลับหน้าหนังสือพิมพ์{" "}
        <span>→</span>
      </Link>
    </div>
  );
}

// "มีประกาศรอชำระเงิน N เรื่อง" on the submit page, so a customer who went back for another notice can still pay
export function BasketBanner() {
  const [basket] = useState(readBasket);
  if (!basket.items.length) return null;
  return (
    <div className="basket-banner">
      <span>มีประกาศรอชำระเงิน {basket.items.length} เรื่อง ({basket.items.map((i) => i.trackingCode).join(", ")})</span>
      <Link className="button dark" to="/newspaper/checkout">
        ไปชำระเงิน{" "}
        <span>→</span>
      </Link>
    </div>
  );
}

function SubmitPage() {
  const { siteContent } = useSiteContent();
  const c = siteContent.submitPage || {};
  const [params] = useSearchParams();
  const method = params.get("method");

  useEffect(() => {
    document.title = "เลือกวิธีลงประกาศหนังสือพิมพ์ | Maneenopkao Group";
  }, []);

  return (
    <main id="main" className="container submit-page method-page">
      <Link className="breadcrumb" to={method ? "/newspaper/submit" : "/newspaper"}>
        {method ? "← เลือกวิธีลงประกาศ" : "← หนังสือพิมพ์"}
      </Link>
      <BasketBanner />
      {method === "template" && <TemplateMethod c={c} />}
      {(method === "file" || method === "excel") && <FileMethod key={method} c={c} method={method} />}
      {!["template", "file", "excel"].includes(method) && <MethodChoice c={c} />}
    </main>
  );
}

export default SubmitPage;
